class TypingText extends HTMLElement {
    constructor() {
        super();
        this.strings = [];
        this.options = {
            typeSpeed: 0.07,
            deleteSpeed: 0.03,
            delayBetweenStrings: 1000,
            loop: true,
            cursorChar: '|'
        };
        
        this.fallbackText = this.getAttribute('fallback-text');
        this.timeoutIds = [];
        this.isAnimating = false;
        this.textNode = null;
        this.cursorElement = null;
    }

    connectedCallback() {
        // Parse attributes
        const stringsAttr = this.getAttribute('strings');
        if (stringsAttr) {
            this.strings = JSON.parse(stringsAttr);
        }

        // Parse options
        const speed = this.getAttribute('type-speed');
        const deleteSpeed = this.getAttribute('delete-speed');
        const delay = this.getAttribute('delay');
        const loop = this.getAttribute('loop');

        if (speed) this.options.typeSpeed = parseFloat(speed);
        if (deleteSpeed) this.options.deleteSpeed = parseFloat(deleteSpeed);
        if (delay) this.options.delayBetweenStrings = parseFloat(delay);
        if (loop !== null) this.options.loop = loop !== 'false';

        // Add CSS for cursor animation only once
        if (!document.getElementById('typing-text-cursor-style')) {
            const style = document.createElement('style');
            style.id = 'typing-text-cursor-style';
            style.textContent = `
                @keyframes cursor-blink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0; }
                }
            `;
            document.head.appendChild(style);
        }

        // Setup DOM structure efficiently
        this.textNode = document.createTextNode('');
        this.cursorElement = document.createElement('span');
        this.cursorElement.textContent = this.options.cursorChar;
        this.cursorElement.style.animation = 'cursor-blink 1s step-end infinite';
        this.cursorElement.style.color = 'var(--tt-main-color)';
        
        // Show fallback text initially to prevent CLS
        const initialText = this.fallbackText || this.textContent.trim() || (this.strings.length > 0 ? this.strings[0] : '');
        this.textNode.textContent = initialText;
        this.textContent = '';
        this.appendChild(this.textNode);
        this.appendChild(this.cursorElement);
        this.#reserveMinWidth();
        
        // Wait for page load before starting animation to minimize CLS
        if (document.readyState === 'complete') {
            const timeoutId = setTimeout(() => this.startAnimation(), 100);
            this.timeoutIds.push(timeoutId);
        } else {
            const loadHandler = () => {
                const timeoutId = setTimeout(() => this.startAnimation(), 100);
                this.timeoutIds.push(timeoutId);
                window.removeEventListener('load', loadHandler);
            };
            window.addEventListener('load', loadHandler);
        }
    }

    disconnectedCallback() {
        // Cleanup to prevent memory leaks
        this.stopAnimation();
    }

    #reserveMinWidth() {
        if (!this.strings.length) return;

        const longest = this.strings.reduce((a, b) => (a.length >= b.length ? a : b));
        const probe = document.createElement('span');
        probe.textContent = longest;
        probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;pointer-events:none';
        probe.style.font = getComputedStyle(this.closest('h1') || this).font;
        document.body.appendChild(probe);
        this.style.minWidth = `${probe.offsetWidth}px`;
        probe.remove();
    }

    stopAnimation() {
        this.isAnimating = false;
        this.timeoutIds.forEach(id => clearTimeout(id));
        this.timeoutIds = [];
    }

    startAnimation() {
        if (this.isAnimating || this.strings.length === 0) return;
        this.isAnimating = true;
        this.currentIndex = 0;
        this.currentStringIndex = 0;
        this.isDeleting = false;
        this.lastUpdateTime = performance.now();
        this.animate();
    }

    animate() {
        if (!this.isAnimating) return;

        const now = performance.now();
        const string = this.strings[this.currentStringIndex];
        
        if (!string) {
            this.isAnimating = false;
            return;
        }

        const delay = this.isDeleting ? this.options.deleteSpeed * 1000 : this.options.typeSpeed * 1000;
        const elapsed = now - this.lastUpdateTime;

        if (elapsed >= delay) {
            if (this.isDeleting) {
                if (this.currentIndex > 0) {
                    this.currentIndex--;
                    this.textNode.textContent = string.substring(0, this.currentIndex);
                    this.lastUpdateTime = now;
                } else {
                    // Finished deleting, move to next string
                    this.isDeleting = false;
                    this.currentStringIndex = (this.currentStringIndex + 1) % this.strings.length;
                    this.lastUpdateTime = now + this.options.delayBetweenStrings;
                }
            } else {
                if (this.currentIndex < string.length) {
                    this.currentIndex++;
                    this.textNode.textContent = string.substring(0, this.currentIndex);
                    this.lastUpdateTime = now;
                } else {
                    // Finished typing, wait then start deleting
                    this.isDeleting = true;
                    this.lastUpdateTime = now + this.options.delayBetweenStrings;
                }
            }
        }

        // Continue animation loop
        if (this.isAnimating) {
            if (this.options.loop || this.currentStringIndex !== 0 || this.currentIndex > 0 || this.isDeleting) {
                requestAnimationFrame(() => this.animate());
            } else {
                this.isAnimating = false;
            }
        }
    }
}

customElements.define('typing-text', TypingText);